import { useState, useEffect, useRef } from 'react';
import { ModalWrapper } from '../../ui';
import type { Ticket, TicketStatus, TicketPriority, TicketSeverity } from '../types';
import { mockTickets, categories, components, users } from '../services/mockData';
import baseAPIService from '../../../../services/baseapiservice';

interface TicketFormProps {
  isOpen: boolean;
  ticketId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadedAttachment {
  file_nm: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  uploaded_at?: string;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function TicketForm({ isOpen, ticketId, onClose, onSuccess }: TicketFormProps) {
  const isEditMode = !!ticketId;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'new' as TicketStatus,
    priority: 'medium' as TicketPriority,
    severity: 'normal' as TicketSeverity,
    category: '',
    component: '',
    version: '',
    assignee: '',
    assignee_email: '',
    tags: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && isEditMode) {
      const ticket = mockTickets.find(t => t.id === ticketId);
      if (ticket) {
        setFormData({
          title: ticket.title,
          description: ticket.description,
          status: ticket.status,
          priority: ticket.priority,
          severity: ticket.severity,
          category: ticket.category,
          component: ticket.component,
          version: ticket.version,
          assignee: ticket.assignee || '',
          assignee_email: ticket.assignee_email || '',
          tags: ticket.tags
        });
      }
    } else if (isOpen) {
      setFormData({
        title: '', description: '', status: 'new', priority: 'medium',
        severity: 'normal', category: '', component: '', version: '',
        assignee: '', assignee_email: '', tags: []
      });
    }
    setErrors({});
    setTagInput('');
    setAttachedFiles([]);
  }, [isOpen, ticketId, isEditMode]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToDelete) }));
  };

  // File attachment handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addFiles = (files: File[]) => {
    const remaining = MAX_FILES - attachedFiles.length;
    const toAdd: File[] = [];
    for (const file of files) {
      if (toAdd.length >= remaining) break;
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, attachments: `File "${file.name}" exceeds 10MB limit` }));
        continue;
      }
      if (!attachedFiles.some(f => f.name === file.name && f.size === file.size)) {
        toAdd.push(file);
      }
    }
    if (toAdd.length > 0) {
      setAttachedFiles(prev => [...prev, ...toAdd]);
      setErrors(prev => ({ ...prev, attachments: '' }));
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const uploadAttachments = async (files: File[]): Promise<UploadedAttachment[]> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await baseAPIService.post('helpdesk/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data || [];
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.title.trim()) e.title = 'Title is required';
    if (!formData.description.trim()) e.description = 'Description is required';
    if (!formData.category) e.category = 'Category is required';
    if (!formData.component) e.component = 'Component is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      // Upload attachments first if any
      let uploadedAttachments: UploadedAttachment[] = [];
      if (attachedFiles.length > 0) {
        setIsUploading(true);
        try {
          uploadedAttachments = await uploadAttachments(attachedFiles);
        } catch (uploadErr: any) {
          console.error('Error uploading files:', uploadErr);
          setErrors(prev => ({ ...prev, attachments: uploadErr?.message || 'Failed to upload attachments' }));
          return;
        } finally {
          setIsUploading(false);
        }
      }

      // TODO: Implement save functionality with attachments
      console.log('Saving ticket:', formData, 'attachments:', uploadedAttachments);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving ticket:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${
      errors[field] ? 'border-red-300 bg-red-50' : 'border-gray-300'
    }`;

  const selectClass = (field: string) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white ${
      errors[field] ? 'border-red-300 bg-red-50' : 'border-gray-300'
    }`;

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Ticket' : 'New Ticket'}
      subtitle="Helpdesk — Support Tickets"
      icon="ri-ticket-2-line"
      gradient="from-blue-600 via-indigo-600 to-purple-600"
      size="xl"
      footerActions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {(isSubmitting || isUploading) ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> {isUploading ? 'Uploading...' : 'Saving...'}</>
            ) : (
              <><i className={isEditMode ? 'ri-save-line' : 'ri-add-line'}></i> {isEditMode ? 'Update' : 'Create'}</>
            )}
          </button>
        </div>
      }
    >
      <div className="p-6 space-y-5">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Brief description of the issue"
            className={inputClass('title')}
          />
          {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={5}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Detailed description of the issue, including steps to reproduce if applicable"
            className={inputClass('description')}
          />
          {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
        </div>

        {/* File Attachments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Attachments
          </label>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const files = Array.from(e.dataTransfer.files);
              addFiles(files);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
              onChange={handleFileSelect}
            />
            <i className="ri-attachment-2 text-2xl text-gray-400 mb-1 block"></i>
            <p className="text-sm text-gray-500">
              Click or drag files here to attach
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Max {MAX_FILES} files, 10MB each
            </p>
          </div>

          {/* Selected File Chips */}
          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {attachedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg text-sm"
                >
                  <i className="ri-file-line text-blue-500"></i>
                  <span className="text-gray-700 max-w-[150px] truncate">{file.name}</span>
                  <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                    className="ml-0.5 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          {errors.attachments && <p className="mt-1 text-xs text-red-600">{errors.attachments}</p>}
        </div>

        {/* Status / Priority / Severity / Version — 4 cols */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={formData.status} onChange={(e) => handleChange('status', e.target.value)} className={selectClass('status')}>
              <option value="new">New</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
              <option value="reopened">Reopened</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select value={formData.priority} onChange={(e) => handleChange('priority', e.target.value)} className={selectClass('priority')}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <select value={formData.severity} onChange={(e) => handleChange('severity', e.target.value)} className={selectClass('severity')}>
              <option value="trivial">Trivial</option>
              <option value="minor">Minor</option>
              <option value="normal">Normal</option>
              <option value="major">Major</option>
              <option value="blocker">Blocker</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => handleChange('version', e.target.value)}
              placeholder="e.g., 2.1.0"
              className={inputClass('version')}
            />
          </div>
        </div>

        {/* Category / Component — 2 cols */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select value={formData.category} onChange={(e) => handleChange('category', e.target.value)} className={selectClass('category')}>
              <option value="">Select category...</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Component <span className="text-red-500">*</span>
            </label>
            <select value={formData.component} onChange={(e) => handleChange('component', e.target.value)} className={selectClass('component')}>
              <option value="">Select component...</option>
              {components.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {errors.component && <p className="mt-1 text-xs text-red-600">{errors.component}</p>}
          </div>
        </div>

        {/* Assignee */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
          <select
            value={formData.assignee}
            onChange={(e) => {
              const user = users.find(u => u.name === e.target.value);
              handleChange('assignee', e.target.value);
              handleChange('assignee_email', user?.email || '');
            }}
            className={selectClass('assignee')}
          >
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.name} value={u.name}>{u.name}</option>)}
          </select>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
              placeholder="Add a tag and press Enter"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="px-3 py-2 text-sm font-medium text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50"
            >
              Add
            </button>
          </div>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {formData.tags.map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {tag}
                  <button type="button" onClick={() => handleDeleteTag(tag)} className="hover:text-blue-900">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
}
